import React, { useState, useEffect } from "react";
import config from "../../config";
import "../../components/UserSelector.css";

// NOTE: This intentionally duplicates the search logic from ../../components/UserSelector.js
// (deliberate, to avoid coupling the two consumers). If you fix a bug here (e.g. debounce,
// error handling), check UserSelector.js too.
export default function UserSearchInput({ onSelectUser, placeholder = "Start typing a name or email..." }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [tpUsers, setTpUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchTpUsers = async () => {
      setIsLoadingUsers(true);
      setErrorMessage("");
      try {
        const response = await fetch(
          `${config.api.baseUrl}/dashboard/active-tp-users`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (data.success) {
          setTpUsers(data.data || []);
        } else {
          throw new Error(data.error || "Failed to fetch users");
        }
      } catch (err) {
        setErrorMessage("Error loading users: " + err.message);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchTpUsers();
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query.length > 1) {
      setIsSearching(true);
      Promise.all([
        Promise.resolve(
          tpUsers.filter(
            (user) =>
              user.email?.toLowerCase().includes(query) ||
              user.firstName?.toLowerCase().includes(query) ||
              user.lastName?.toLowerCase().includes(query)
          )
        ),
        fetch(
          `${config.api.baseUrl}/auth/users/local-search?q=${encodeURIComponent(query)}`,
          { credentials: "include" }
        )
          .then((r) => r.json())
          .then((d) => (d && d.success && Array.isArray(d.users) ? d.users : []))
          .catch(() => []),
      ]).then(([tp, local]) => {
        const merged = [...tp, ...local]
          .filter(
            (u, idx, arr) =>
              idx === arr.findIndex(
                (x) => x.email && x.email.toLowerCase() === (u.email || "").toLowerCase()
              )
          )
          .slice(0, 8);
        setSearchResults(merged);
      });
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  const handleSelectUser = (e, user) => {
    e.preventDefault();
    e.stopPropagation();
    onSelectUser(user);
    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
  };

  return (
    <div className="user-search-container">
      <input
        type="text"
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder={placeholder}
        className="user-search-input"
      />

      {isLoadingUsers && <div className="loading-message">Loading users...</div>}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {isSearching && searchResults.length > 0 && (
        <ul className="user-search-results">
          {searchResults.map((user) => (
            <li
              key={user.email}
              onClick={(e) => handleSelectUser(e, user)}
              className="user-search-result"
            >
              {user.avatarUri ? (
                <img
                  src={`${config.api.baseUrl}${config.api.endpoints.dashboard.tpAvatar}?url=${encodeURIComponent(user.avatarUri)}`}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="user-avatar"
                />
              ) : (
                <span
                  className="user-avatar-placeholder"
                  dangerouslySetInnerHTML={{
                    __html: `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 40 40'>
  <circle cx='20' cy='20' r='20' fill='#3469ff'/>
  <text x='50%' y='50%' fill='#ffffff' font-family='Roboto, sans-serif' font-size='14' font-weight='600' text-anchor='middle' dominant-baseline='central'>${`${(user.firstName || "?").charAt(0).toUpperCase()}${(user.lastName || "?").charAt(0).toUpperCase()}`}</text>
</svg>`,
                  }}
                />
              )}
              <div className="user-info">
                <div className="user-name">{user.firstName} {user.lastName}</div>
                <div className="user-email">{user.email}</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {isSearching && searchResults.length === 0 && searchQuery.length > 1 && (
        <div className="no-results">No matching users found</div>
      )}
    </div>
  );
}
